import {
  getGetRestaurantTablesQueryKey,
  getGetRestaurantZonesQueryKey,
  useDeleteRestaurantZonesZoneId,
  usePatchRestaurantZonesZoneId,
  usePostRestaurantZones,
} from '@agos/api-client/orders'
import { useGetFiskalRegisters } from '@agos/api-client/fiskal'
import { T, registerIds, registerName, tt } from '@agos/shared'
import { BUTTON_GHOST, BUTTON_PRIMARY, cn, ConfirmDialog, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, Receipt, Trash2, X } from 'lucide-react'
import { useState } from 'react'

import {
  FOLLOWS_CHANNEL,
  zoneNameMessage,
  zoneNameProblem,
  type ZoneOption,
} from './zones'

/**
 * The zones of the dining room, and the register each one prints on - `FRONTEND.md` §9.337.
 *
 * **The owner named this screen:** *"in mapovanie stolov add support to WHICH kasa use.. for example
 * zóna Reštaurácia and Zóna Terasa - terasa should have different kasa then reštaurácia"*. Their
 * example is two zones of **one channel**, which is why `BACKEND.md` §10A.125 puts the zone's
 * register above the channel's.
 *
 * **Drawn for a `main_admin` only.** `POST`/`PATCH`/`DELETE /restaurant-zones` are
 * `@RequireAuth("main_admin")` while the list is any signed-in staff - and offering a waiter a form
 * the server refuses is FB-001's failure on a screen a waiter uses all day.
 */

const FIELD =
  'w-full px-2 py-1.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'

/** What the two shipped registers are called before `GET /fiskal/registers` answers. */
const SHIPPED_REGISTER_NAMES: Record<string, string> = {
  restauracia: tt('Kasa Reštaurácia'),
  rozvoz: tt('Kasa Rozvoz'),
}

export function ZonesPanel({ zones, onClose }: { zones: ZoneOption[]; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [newName, setNewName] = useState('')
  const [pendingRetire, setPendingRetire] = useState<ZoneOption | null>(null)

  const { data: registerData } = useGetFiskalRegisters()
  const registers = Array.isArray(registerData)
    ? (registerData as Array<{ kasaId?: string; label?: string }>)
    : []
  const ids = registerIds(registerData)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetRestaurantZonesQueryKey() })
    // The chips count tables per zone, so a retired zone changes the table list's grouping too.
    void queryClient.invalidateQueries({ queryKey: getGetRestaurantTablesQueryKey() })
  }
  const failed = (title: string) => () =>
    toast({ variant: 'destructive', title, description: '' })

  const create = usePostRestaurantZones()
  const update = usePatchRestaurantZonesZoneId()
  const retire = useDeleteRestaurantZonesZoneId()

  const nameProblem = newName.trim() === '' ? null : zoneNameProblem(newName, zones)

  const add = () => {
    if (zoneNameProblem(newName, zones) !== null) return
    create.mutate(
      { data: { name: newName.trim() } },
      {
        onSuccess: () => {
          setNewName('')
          refresh()
        },
        onError: failed(tt('Zónu sa nepodarilo vytvoriť')),
      },
    )
  }

  /**
   * One field at a time, because `PATCH` reads null as *not mentioned*.
   *
   * `RestaurantZoneService.update` only touches a field when it is non-null, so sending the whole
   * row would be no safer and sending one key is what the endpoint is shaped for. An **empty string
   * clears** - which is how *Podľa kanála* is chosen again after a register was set.
   */
  const patch = (zone: ZoneOption, data: { name?: string; color?: string; kasaId?: string }) => {
    if (zone.id === null) return
    update.mutate(
      { zoneId: zone.id, data },
      { onSuccess: refresh, onError: failed(tt('Zmenu sa nepodarilo uložiť')) },
    )
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-4 space-y-3" data-zones-panel>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold flex items-center gap-1.5">
          <Receipt className="w-4 h-4 text-muted-foreground" /> <T>Zóny a pokladne</T>
        </h3>
        <button type="button" aria-label={tt('Zatvoriť')} onClick={onClose} className={BUTTON_GHOST}>
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-[11px] text-muted-foreground">
        <T>
          Doklady zo stolov v zóne sa vytlačia na jej pokladni. Bez zvolenej pokladne rozhoduje
          predajný kanál.
        </T>
      </p>

      <div className="flex gap-2">
        <input
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') add()
          }}
          placeholder={tt('Názov novej zóny, napr. Terasa')}
          aria-label={tt('Názov novej zóny')}
          className={FIELD}
        />
        <button
          type="button"
          onClick={add}
          disabled={zoneNameProblem(newName, zones) !== null || create.isPending}
          data-zone-add
          className={cn(
            'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold shrink-0',
            BUTTON_PRIMARY,
            'disabled:opacity-40 disabled:cursor-not-allowed',
          )}
        >
          <Plus className="w-3.5 h-3.5" /> <T>Pridať zónu</T>
        </button>
      </div>
      {nameProblem !== null && (
        <p className="text-xs text-red-500" data-zone-name-problem>
          {zoneNameMessage(nameProblem)}
        </p>
      )}

      {zones.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          <T>Žiadne zóny. Pridaj prvú a potom prirad stoly v paneli stola.</T>
        </p>
      ) : (
        <div className="space-y-2">
          {zones.map((zone) => (
            <div
              key={zone.id === null ? `t:${zone.name}` : `z:${zone.id}`}
              data-zone-row={zone.id ?? ''}
              className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center rounded-xl border border-border bg-muted/30 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{zone.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {zone.tables} {tt('stolov')}
                  {zone.id === null && (
                    <>
                      {' · '}
                      {/* §10A.117 kept `zone` as text beside `zone_id`, so this state is real and
                        * has to be legible: a name typed on a table, with no zone row behind it and
                        * therefore nothing a register can hang off. */}
                      <span className="text-amber-500">
                        <T>len text na stole - bez zóny</T>
                      </span>
                    </>
                  )}
                </p>
              </div>

              {zone.id === null ? (
                <p className="text-[11px] text-muted-foreground sm:col-span-3">
                  <T>Vytvor zónu s týmto názvom a prirad k nej stoly, aby jej šla pokladňa.</T>
                </p>
              ) : (
                <>
                  <select
                    value={zone.kasaId ?? ''}
                    onChange={(event) => patch(zone, { kasaId: event.target.value })}
                    aria-label={`${tt('Pokladňa pre zónu')} ${zone.name}`}
                    data-zone-kasa={zone.id}
                    className={cn(FIELD, 'sm:w-52')}
                  >
                    <option value="">{FOLLOWS_CHANNEL}</option>
                    {ids.map((id) => (
                      <option key={id} value={id}>
                        {registerName(id, registers, SHIPPED_REGISTER_NAMES)}
                      </option>
                    ))}
                  </select>

                  <input
                    type="color"
                    value={zone.color ?? '#888888'}
                    onChange={(event) => patch(zone, { color: event.target.value })}
                    aria-label={`${tt('Farba zóny')} ${zone.name}`}
                    className="h-9 w-10 rounded-lg border border-border bg-background"
                  />

                  <button
                    type="button"
                    onClick={() => setPendingRetire(zone)}
                    aria-label={`${tt('Vyradiť zónu')} ${zone.name}`}
                    data-zone-retire={zone.id}
                    className="flex items-center justify-center p-2 rounded-lg bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* The endpoint deactivates rather than deletes - §10A.117: `zone_id` is `ON DELETE SET NULL`,
        * so a real delete would take the zone off every table in it with no way to tell that from
        * somebody clearing them by hand. The question carries the count the list already knows. */}
      {pendingRetire !== null && (
        <ConfirmDialog
          open
          title={tt('Vyradiť zónu')}
          description={`${tt('Zóna')} „${pendingRetire.name}“ ${tt('sa prestane používať. Jej')} ${pendingRetire.tables} ${tt('stolov zostáva - doklady im bude určovať predajný kanál.')}`}
          confirmLabel={tt('Vyradiť')}
          variant="danger"
          onClose={() => setPendingRetire(null)}
          onConfirm={() => {
            const target = pendingRetire
            setPendingRetire(null)
            if (target.id === null) return
            retire.mutate(
              { zoneId: target.id },
              { onSuccess: refresh, onError: failed(tt('Vyradenie zlyhalo')) },
            )
          }}
        />
      )}
    </div>
  )
}
